import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'hooks/useDebounce';
import { ApiResponse, CandidateListQueryParam } from 'modules/Recruit/types/api';
import React, { useEffect, useState } from 'react';
import { Alert } from 'subframe/components/Alert';
import { IconButton } from 'subframe/components/IconButton';
import endpoints from '../../constants/endpoints';
import { getRecruitHomeRoute } from '../../constants/routes';
import { getCandidatesList } from '../../services/recruit';
import { BreadcrumbItem, CandidateListFilter, CandidateListResponse, IconName } from '../../types';
import { Breadcrumb } from '../CandidateProfile/components';
import { CandidateListFilters, CandidateListTable } from './components';
import { candidateListData } from './data';

export default function CandidateListPage() {
  const [pageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<CandidateListFilter>(candidateListData.filters);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    filters.region,
    filters.location,
    filters.stage,
    filters.rankBy,
    filters.order,
  ]);

  const apiParams: CandidateListQueryParam = {
    sort_by: filters.rankBy,
    order: filters.order,
    page: currentPage,
    page_size: pageSize,
    search_term: debouncedSearch,
  };

  if (filters.region && filters.region !== 'all') {
    apiParams.region = filters.region;
  }

  if (filters.location && filters.location !== 'all') {
    apiParams.location = filters.location;
  }

  const { data, isLoading, error, isError } = useQuery<ApiResponse<CandidateListResponse>>({
    queryKey: [endpoints.candidateList, apiParams],
    queryFn: () => getCandidatesList(apiParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const handleFilterChange = (newFilters: Partial<CandidateListFilter>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      icon: IconName.FEATHER_USERS,
      label: 'Candidates',
      href: getRecruitHomeRoute(),
    },
  ];

  const pagination = data?.result?.pagination;
  const candidates = data?.result?.candidates || [];

  if (isError) {
    return (
      <div className="flex h-full w-full flex-col items-start gap-2">
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-4 md:gap-8 bg-default-background px-4 md:px-12 pt-6 pb-12">
          <Alert variant="error" title="Error loading candidates">
            {error?.message || 'Failed to load candidate data. Please try again.'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-start gap-2">
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-4 md:gap-8 bg-default-background px-4 md:px-12 pt-6 pb-12">
        <CandidateListFilters filters={filters} onFilterChange={handleFilterChange} />

        <CandidateListTable rows={candidates} filters={filters} loading={isLoading} />

        {pagination && (
          <div className="flex w-full flex-col items-center gap-4 pt-2">
            <div className="flex max-w-[1280px] items-center justify-between gap-1">
              <div className="flex items-center justify-end gap-1">
                <IconButton
                  variant="neutral-secondary"
                  icon="FeatherChevronLeft"
                  disabled={!pagination.has_previous}
                  onClick={() => handlePageChange(pagination.page - 1)}
                />
                <div className="flex h-8 items-center justify-center gap-1 bg-neutral-100 px-2 py-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {pagination.page} of {pagination.total_pages}
                  </span>
                </div>
                <IconButton
                  variant="neutral-secondary"
                  icon="FeatherChevronRight"
                  disabled={!pagination.has_next}
                  onClick={() => handlePageChange(pagination.page + 1)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
